import type { Contact, Company, Deal } from '@paintscout/api'
import type { OptionsDocument } from 'paintscout'
import { mapLeadSourceToPublic, hasLeadSources } from '@paintscout/util/builder'

export interface SourceDetail {
  title: string
  value: string
  canCopy?: boolean
}

interface MappedSourceDetailArgs {
  title: string
  leadSource?: string | null
  options: OptionsDocument
  canCopy?: boolean
}

export function getMappedSourceDetail({
  title,
  leadSource,
  options,
  canCopy
}: MappedSourceDetailArgs): SourceDetail | null {
  if (!hasLeadSources(options)) {
    return null
  }

  const normalizedLeadSource = (leadSource ?? '').trim()
  if (!normalizedLeadSource) {
    return null
  }

  const label = mapLeadSourceToPublic({ options, lead_source: normalizedLeadSource })
  if (!label) {
    return null
  }

  return {
    title,
    value: label,
    canCopy
  }
}

export function getContactSourceDetail({
  contact,
  options,
  hasSources
}: {
  contact?: Contact | null
  options: OptionsDocument
  hasSources: boolean
}): SourceDetail | null {
  if (!hasSources) {
    return null
  }

  return getMappedSourceDetail({
    title: 'Source',
    leadSource: contact?.lead_source,
    options
  })
}

export function getCompanySourceDetail({
  company,
  options,
  hasSources
}: {
  company?: Company | null
  options: OptionsDocument
  hasSources: boolean
}): SourceDetail | null {
  if (!hasSources) {
    return null
  }

  return getMappedSourceDetail({
    title: 'Source',
    leadSource: company?.lead_source,
    options,
    canCopy: true
  })
}

export function getDealLeadSourceDetail({
  deal,
  options
}: {
  deal?: Deal | null
  options: OptionsDocument
}): SourceDetail | null {
  return getMappedSourceDetail({
    title: 'Lead Source',
    leadSource: deal?.lead_source,
    options
  })
}

export function getDealAcquisitionSourceDetail({
  deal,
  options
}: {
  deal?: Deal | null
  options: OptionsDocument
}): SourceDetail | null {
  const acquisition = (deal as any)?.acquisitionSource ?? ''
  return getMappedSourceDetail({
    title: 'Acquisition Source',
    leadSource: acquisition,
    options
  })
}
